import { useQuery, requestDeals } from '@workspace/api';

export interface TaskStateHistory {
    from_status: string | null;
    to_status: string;
    changed_at: string;
    metadata: Record<string, unknown> | null;
}

export interface TaskQuoteSummary {
    etat: string;
    statut: string;
    contact_nom: string | null;
    contact_prenom: string | null;
}

export interface Task {
    id: number;
    quote_id: number | null;
    id_devis: number | null;
    contact_id: number | null;
    subject: string;
    description: string | null;
    telephone: string | null;
    status: string;
    due_date: string | null;
    created_at: string;
    updated_at: string;
    quote_summary?: TaskQuoteSummary;
    history?: TaskStateHistory[];
    call_notifications?: import('./quotes').CallNotification[];
}

export interface TaskFilters {
    search?: string;
    status?: string;
    quote_id?: number;
    created_from?: string;
    created_to?: string;
    per_page?: number;
    page?: number;
    sort?: string;
    [key: string]: string | number | boolean | undefined;
}

export interface TasksResponse {
    data: Task[];
    links?: {
        first: string | null;
        last: string | null;
        prev: string | null;
        next: string | null;
    };
    meta?: {
        current_page: number;
        from: number | null;
        last_page: number;
        per_page: number;
        to: number | null;
        total: number;
    };
}

/**
 * Hook pour récupérer la liste des tâches avec filtres et pagination
 */
export function useTasks(filters: TaskFilters = {}) {
    const params: Record<string, string | number | boolean> = {};
    if (filters.search) params['filter[search]'] = filters.search;
    if (filters.status) params['filter[status]'] = filters.status;
    if (filters.quote_id) params['filter[quote_id]'] = filters.quote_id;
    if (filters.created_from) params['filter[created_from]'] = filters.created_from;
    if (filters.created_to) params['filter[created_to]'] = filters.created_to;
    if (filters.sort) params.sort = filters.sort;
    params['page[size]'] = filters.per_page || 50;
    params['page[number]'] = filters.page || 1;

    return useQuery<TasksResponse>({
        queryKey: ['deals', 'tasks', filters],
        queryFn: async () => {
            const response = await requestDeals.get<TasksResponse>('/api/tasks', { params });
            return response.data;
        },
        staleTime: 30 * 1000,
    });
}

/**
 * Hook pour récupérer une tâche unique par son ID
 */
export function useTask(id: string | number) {
    return useQuery<{ data: Task }>({
        queryKey: ['deals', 'tasks', id],
        queryFn: async () => {
            const response = await requestDeals.get<{ data: Task }>(`/api/tasks/${id}`);
            return response.data;
        },
        staleTime: 5 * 60 * 1000,
        enabled: !!id,
    });
}

/**
 * Hook pour récupérer les tâches d'un contact
 */
export function useContactTasks(contactId: string | number) {
    return useQuery<TasksResponse>({
        queryKey: ['deals', 'contacts', contactId, 'tasks'],
        queryFn: async () => {
            const response = await requestDeals.get<TasksResponse>(`/api/contacts/${contactId}/tasks`, {
                params: { per_page: 100 },
            });
            return response.data;
        },
        staleTime: 30 * 1000,
        enabled: !!contactId,
    });
}

/**
 * Hook pour récupérer les tâches d'un devis Deals (quote_id local)
 */
export function useQuoteTasks(quoteId: number | null | undefined) {
    return useQuery<TasksResponse>({
        queryKey: ['deals', 'quotes', quoteId, 'tasks'],
        queryFn: async () => {
            const response = await requestDeals.get<TasksResponse>('/api/tasks', {
                params: {
                    'filter[quote_id]': quoteId,
                    'page[size]': 100,
                    sort: '-created_at',
                },
            });
            return response.data;
        },
        staleTime: 30 * 1000,
        enabled: !!quoteId,
    });
}

/**
 * Labels pour les statuts de tâche
 */
export const taskStatusLabels: Record<string, string> = {
    attente: 'En attente',
    rappel: 'En cours de rappel',
    reporte: 'Reporté',
    termine: 'Terminé',
    annule: 'Annulé',
};

/**
 * Exporte les tâches en CSV selon les filtres actifs
 */
export async function exportTasksCsv(filters: TaskFilters = {}): Promise<void> {
    const params: Record<string, string | number | boolean> = {};
    if (filters.search) params['filter[search]'] = filters.search;
    if (filters.status) params['filter[status]'] = filters.status;
    if (filters.quote_id) params['filter[quote_id]'] = filters.quote_id;
    if (filters.created_from) params['filter[created_from]'] = filters.created_from;
    if (filters.created_to) params['filter[created_to]'] = filters.created_to;

    const response = await requestDeals.get('/api/tasks/export', {
        params,
        responseType: 'blob',
    });

    const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;

    const disposition = response.headers['content-disposition'];
    const filename = disposition?.match(/filename="?(.+?)"?$/)?.[1] || 'taches_export.csv';
    link.download = filename;

    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
}

/**
 * Couleurs pour les statuts de tâche
 */
export const taskStatusColors: Record<string, string> = {
    attente: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
    rappel: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
    reporte: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300',
    termine: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
    annule: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300',
};
